import React, { useState, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { Button, Card, CardContent, Typography, TextField, Select, MenuItem, IconButton, Collapse } from '@material-ui/core';
import Icon from '@material-ui/core/Icon';
import Http from '../../../../Http';
import AutocompleteAsyncrono from '../../../../components/Autocomplete';
import Toast from 'react-hot-toast';

// ========================= CAMPOS Y OPERADORES =========================

// Campos disponibles para esquema Guia y reglas de grupo en Embalaje (SIN embalaje fields)
const GUIA_FIELDS = {
  CLIENTE: ['=', '<>'],
  'KM ENTREGA': ['=', '>', '<', '>=', '<=', '<>'],
  'SUCURSAL DESTINO': ['=', '<>'],
  'ZONA DESTINO': ['=', '<>'],
  'TIPO ZONA DESTINO': ['=', '<>'],
  TRANSPORTISTA: ['=', '<>'],
  CHOFER: ['=', '<>', 'ES ALGUNO'],
  'PIEZAS TOTALES': ['=', '>', '<', '>=', '<=', '<>'],
  'PESO TOTAL': ['=', '>', '<', '>=', '<=', '<>'],
  'VALOR TOTAL': ['=', '>', '<', '>=', '<=', '<>'],
  'VALOR ENTREGA': ['=', '>', '<', '>=', '<=', '<>'],
  'VALOR FLETE': ['=', '>', '<', '>=', '<=', '<>'],
  'ES AUTOSERVICIO NORMAL': [],
  'ES AUTOSERVICIO NOCTURNO': [],
  'ES AUTOSERVICIO DIAS FESTIVOS': [],
  'FECHA GUIA': ['=', '>', '<', '>=', '<=', '<>'],
  'HORA GUIA': ['=', '>', '<', '>=', '<=', '<>'],
  'FECHA CITA': ['=', '>', '<', '>=', '<=', '<>'],
  'HORA CITA': ['=', '>', '<', '>=', '<=', '<>'],
};

// Sub-reglas para definiciones de embalaje
const EMBALAJE_SUB_FIELDS = {
  'PIEZAS EMBALAJE': ['=', '>', '<', '>=', '<=', '<>'],
  'PESO EMBALAJE': ['=', '>', '<', '>=', '<=', '<>'],
};

const timeFields = ['HORA GUIA', 'HORA CITA'];
const dateFields = ['FECHA GUIA', 'FECHA CITA'];

// ID 1 = comodin CUALQUIERA
const EMBALAJE_COMODIN_ID = 1;

// ========================= COMPONENTE PRINCIPAL =========================

export default function RuleBuilder() {
  const [catalogs, setCatalogs] = useState({ sucursales: [], zonas: [], zona_tipos: [], embalajes: [], transportistas: [] });
  const [state, setState] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const init = async () => {
      const cats = await fetchCatalogs();
      if (cats) await loadRules(cats);
    };
    init();
  }, []);

  const fetchCatalogs = async () => {
    try {
      const res = await Http.get('pago-transportista-reglas/form-catalogs');
      if (res && res.data) {
        setCatalogs(res.data);
        return res.data;
      }
    } catch (e) {
      console.error(e);
      Toast.error("Error al cargar catalogos");
    }
    return null;
  };

  const loadRules = async (loadedCats) => {
    try {
      setLoading(true);
      const res = await Http.get('pago-transportista-reglas/load');
      if (res && res.data) {
        const loaded = res.data.map(grupo => {
          if (grupo.esquema === 'Embalaje') {
            return {
              ...grupo,
              grupo_reglas: (grupo.grupo_reglas || []).map(r => ({
                ...r,
                operador: r.operador === 'IN' ? 'ES ALGUNO' : r.operador,
                valoresMultiples: r.operador === 'IN' ? String(r.valor).split(',').map(v => {
                  const val = v.trim();
                  const fromOpts = (r.opcionesAutocomplete || []).find(o => String(o.Value) === String(val));
                  return { Value: val, Label: fromOpts ? fromOpts.Label : val };
                }) : [],
                opcionesAutocomplete: r.opcionesAutocomplete || [],
              })),
              embalaje_defs: (grupo.embalaje_defs || []).map(def => ({
                ...def,
                operador_embalaje: def.operador_embalaje === 'IN' ? 'ES ALGUNO' : def.operador_embalaje,
                valoresMultiples: def.operador_embalaje === 'IN'
                  ? String(def.valor_embalaje).split(',').map(v => ({ Value: v.trim(), Label: (loadedCats.embalajes.find(e => String(e.Value) === v.trim()) || {}).Label || v.trim() }))
                  : [],
                subreglas: (def.subreglas || []).map(sr => ({ ...sr })),
              })),
              isEditing: false,
              collapsed: true,
            };
          }
          // Esquema Guia
          return {
            ...grupo,
            isEditing: false,
            collapsed: true,
            subgrupos: (grupo.subgrupos || []).map(sg => ({
              ...sg,
              reglas: (sg.reglas || []).map(r => ({
                ...r,
                operador: r.operador === 'IN' ? 'ES ALGUNO' : r.operador,
                valoresMultiples: r.operador === 'IN' ? String(r.valor).split(',').map(v => {
                  const val = v.trim();
                  const fromOpts = (r.opcionesAutocomplete || []).find(o => String(o.Value) === String(val));
                  return { Value: val, Label: fromOpts ? fromOpts.Label : val };
                }) : [],
                opcionesAutocomplete: r.opcionesAutocomplete || [],
              })),
            })),
          };
        });
        setState(loaded);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  // ========================= STATE MUTATIONS =========================

  const cloneState = () => JSON.parse(JSON.stringify(state));

  // --- GROUPS ---
  const addGroup = (esquema = 'Guia') => {
    const base = {
      id: `group-${Date.now()}`,
      nombre_grupo: esquema === 'Embalaje' ? 'Nuevo Grupo Embalaje' : 'Nuevo Grupo',
      esquema,
      isEditing: true,
      collapsed: false,
    };
    if (esquema === 'Embalaje') {
      base.grupo_reglas = [];
      base.embalaje_defs = [];
    } else {
      base.subgrupos = [];
    }
    setState([...state, base]);
  };

  const updateGroupName = (gIndex, val) => {
    const ns = cloneState();
    ns[gIndex].nombre_grupo = val;
    setState(ns);
  };

  const toggleGroupEdit = (gIndex) => {
    const ns = cloneState();
    ns[gIndex].isEditing = !ns[gIndex].isEditing;
    if (ns[gIndex].isEditing) ns[gIndex].collapsed = false; // Auto expand on edit
    setState(ns);
  };

  const toggleGroupCollapse = (gIndex) => {
    const ns = cloneState();
    ns[gIndex].collapsed = !ns[gIndex].collapsed;
    setState(ns);
  };

  const removeGroup = (gIndex) => {
    const ns = cloneState();
    ns.splice(gIndex, 1);
    setState(ns);
  };

  // --- GUIA: SUBGROUPS ---
  const addSubGroup = (gIndex) => {
    const ns = cloneState();
    ns[gIndex].subgrupos.push({
      id: `subgroup-${Date.now()}`,
      tipo_pago: 'Monto fijo',
      valor_pago: 0,
      reglas: []
    });
    setState(ns);
  };

  const removeSubGroup = (gIndex, sgIndex) => {
    const ns = cloneState();
    ns[gIndex].subgrupos.splice(sgIndex, 1);
    setState(ns);
  };

  const updateSubGroup = (gIndex, sgIndex, key, val) => {
    const ns = cloneState();
    ns[gIndex].subgrupos[sgIndex][key] = val;
    setState(ns);
  };

  // --- GUIA: RULES ---
  const addRule = (gIndex, sgIndex) => {
    const ns = cloneState();
    ns[gIndex].subgrupos[sgIndex].reglas.push({
      id: `rule-${Date.now()}`,
      campo: 'CLIENTE',
      operador: '=',
      valor: '',
      valoresMultiples: [],
      opcionesAutocomplete: [],
    });
    setState(ns);
  };

  const updateRule = (gIndex, sgIndex, rIndex, key, val) => {
    const ns = cloneState();
    const rule = ns[gIndex].subgrupos[sgIndex].reglas[rIndex];
    rule[key] = val;
    if (key === 'campo') {
      rule.operador = GUIA_FIELDS[val]?.[0] || '';
      rule.valor = '';
      rule.valoresMultiples = [];
    }
    setState(ns);
  };

  const removeRule = (gIndex, sgIndex, rIndex) => {
    const ns = cloneState();
    ns[gIndex].subgrupos[sgIndex].reglas.splice(rIndex, 1);
    setState(ns);
  };

  // --- EMBALAJE: GROUP RULES ---
  const addGrupoRegla = (gIndex) => {
    const ns = cloneState();
    ns[gIndex].grupo_reglas.push({
      id: `gr-${Date.now()}`,
      campo: 'CLIENTE',
      operador: '=',
      valor: '',
      valoresMultiples: [],
      opcionesAutocomplete: [],
    });
    setState(ns);
  };

  const updateGrupoRegla = (gIndex, rIndex, key, val) => {
    const ns = cloneState();
    const rule = ns[gIndex].grupo_reglas[rIndex];
    rule[key] = val;
    if (key === 'campo') {
      rule.operador = GUIA_FIELDS[val]?.[0] || '';
      rule.valor = '';
      rule.valoresMultiples = [];
    }
    setState(ns);
  };

  const removeGrupoRegla = (gIndex, rIndex) => {
    const ns = cloneState();
    ns[gIndex].grupo_reglas.splice(rIndex, 1);
    setState(ns);
  };

  // --- EMBALAJE: DEFINITIONS ---
  const addEmbalajeDef = (gIndex) => {
    const ns = cloneState();
    ns[gIndex].embalaje_defs.push({
      id: `embdef-${Date.now()}`,
      operador_embalaje: '=',
      valor_embalaje: '',
      valoresMultiples: [],
      tipo_pago: 'Monto fijo',
      valor_pago: 0,
      subreglas: [],
    });
    setState(ns);
  };

  const updateEmbalajeDef = (gIndex, dIndex, key, val) => {
    const ns = cloneState();
    const def = ns[gIndex].embalaje_defs[dIndex];
    def[key] = val;
    // Reset valores al cambiar operador
    if (key === 'operador_embalaje') {
      def.valor_embalaje = '';
      def.valoresMultiples = [];
    }
    setState(ns);
  };

  const removeEmbalajeDef = (gIndex, dIndex) => {
    const ns = cloneState();
    ns[gIndex].embalaje_defs.splice(dIndex, 1);
    setState(ns);
  };

  // --- EMBALAJE: SUBRULES ---
  const addEmbalajeSubregla = (gIndex, dIndex) => {
    const ns = cloneState();
    ns[gIndex].embalaje_defs[dIndex].subreglas.push({
      id: `sr-${Date.now()}`,
      campo: 'PIEZAS EMBALAJE',
      operador: '=',
      valor: '',
    });
    setState(ns);
  };

  const updateEmbalajeSubregla = (gIndex, dIndex, srIndex, key, val) => {
    const ns = cloneState();
    const sr = ns[gIndex].embalaje_defs[dIndex].subreglas[srIndex];
    sr[key] = val;
    if (key === 'campo') {
      sr.operador = EMBALAJE_SUB_FIELDS[val]?.[0] || '=';
      sr.valor = '';
    }
    setState(ns);
  };

  const removeEmbalajeSubregla = (gIndex, dIndex, srIndex) => {
    const ns = cloneState();
    ns[gIndex].embalaje_defs[dIndex].subreglas.splice(srIndex, 1);
    setState(ns);
  };

  // ========================= SAVE =========================

  const saveRules = async () => {
    // Validacion: grupos Embalaje deben tener al menos 1 embalaje_def
    for (const grupo of state) {
      if (grupo.esquema === 'Embalaje' && (!grupo.embalaje_defs || grupo.embalaje_defs.length === 0)) {
        Toast.error(`El grupo "${grupo.nombre_grupo}" requiere al menos una definición de embalaje`);
        return;
      }
    }

    try {
      const payload = {
        grupos: state.map(grupo => {
          if (grupo.esquema === 'Embalaje') {
            return {
              nombre_grupo: grupo.nombre_grupo,
              esquema: 'Embalaje',
              grupo_reglas: (grupo.grupo_reglas || []).map(r => ({
                campo: r.campo,
                operador: r.operador === 'ES ALGUNO' ? 'IN' : r.operador,
                valor: r.operador === 'ES ALGUNO' ? r.valoresMultiples.map(v => v.Value) : r.valor,
              })),
              embalaje_defs: (grupo.embalaje_defs || []).map(def => ({
                operador_embalaje: def.operador_embalaje === 'ES ALGUNO' ? 'IN' : def.operador_embalaje,
                valor_embalaje: def.operador_embalaje === 'ES ALGUNO'
                  ? (def.valoresMultiples || []).map(v => v.Value)
                  : def.valor_embalaje,
                tipo_pago: def.tipo_pago,
                valor_pago: def.valor_pago,
                subreglas: (def.subreglas || []).map(sr => ({
                  campo: sr.campo,
                  operador: sr.operador,
                  valor: sr.valor,
                })),
              })),
            };
          }
          // Guia
          return {
            nombre_grupo: grupo.nombre_grupo,
            esquema: 'Guia',
            subgrupos: (grupo.subgrupos || []).map(sg => ({
              tipo_pago: sg.tipo_pago,
              valor_pago: sg.valor_pago,
              reglas: (sg.reglas || []).map(r => ({
                campo: r.campo,
                operador: r.operador === 'ES ALGUNO' ? 'IN' : r.operador,
                valor: r.operador === 'ES ALGUNO' ? r.valoresMultiples.map(v => v.Value) : r.valor,
              })),
            })),
          };
        })
      };

      const res = await Http.post('pago-transportista-reglas/save', payload);
      if (res && !res.error) {
        Toast.success("Reglas guardadas correctamente");
        loadRules(catalogs);
      } else {
        Toast.error(res?.errMsg || "Error al guardar reglas");
      }
    } catch (e) {
      Toast.error("Error al guardar reglas");
    }
  };

  // ========================= DRAG & DROP =========================

  const onDragEnd = (result) => {
    const { source, destination, type } = result;
    if (!destination) return;
    if (source.droppableId === destination.droppableId && source.index === destination.index) return;

    const ns = cloneState();

    if (type === 'group') {
      const [removed] = ns.splice(source.index, 1);
      ns.splice(destination.index, 0, removed);
      setState(ns);
      return;
    }

    if (type === 'rule') {
      const [sourceG, sourceSG] = source.droppableId.replace('guia-', '').split('-').map(Number);
      const [destG, destSG] = destination.droppableId.replace('guia-', '').split('-').map(Number);
      const src = ns[sourceG].subgrupos[sourceSG];
      const dst = ns[destG].subgrupos[destSG];
      const [removedRule] = src.reglas.splice(source.index, 1);
      dst.reglas.splice(destination.index, 0, removedRule);
      setState(ns);
      return;
    }

    if (type === 'embalaje-def') {
      const gIndex = Number(source.droppableId.replace('embdefs-', ''));
      const defs = ns[gIndex].embalaje_defs;
      const [removed] = defs.splice(source.index, 1);
      defs.splice(destination.index, 0, removed);
      setState(ns);
      return;
    }
  };

  // ========================= RENDER VALUE INPUT (shared) =========================

  const renderValueInput = (rule, onChange, catalogs_ref, isEditing) => {
    const isBoolean = rule.campo.startsWith('ES AUTOSERVICIO');
    if (isBoolean) return <span style={{ color: '#999', fontSize: 13 }}>Campo Booleano</span>;

    if (dateFields.includes(rule.campo)) {
      return <TextField disabled={!isEditing} type="date" value={rule.valor || ''} onChange={e => onChange("valor", e.target.value)} InputLabelProps={{ shrink: true }} style={{ width: 180 }} />;
    }

    if (timeFields.includes(rule.campo)) {
      const parts = (rule.valor || '').split(' ');
      const timeVal = parts[0] || '';
      const ampm = parts[1] || 'AM';
      const handleT = (t, ap) => onChange("valor", `${t} ${ap}`);
      return (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <TextField disabled={!isEditing} type="text" placeholder="HH:MM" value={timeVal}
            onChange={e => {
              let v = e.target.value.replace(/[^0-9:]/g, '');
              if (v.length === 2 && !v.includes(':')) v += ':';
              if (v.length > 5) v = v.substring(0, 5);
              const p = v.split(':');
              if (p[0] && parseInt(p[0]) > 12) p[0] = '12';
              if (p[1] && parseInt(p[1]) > 59) p[1] = '59';
              handleT(p.join(':'), ampm);
            }}
            style={{ width: 80 }} inputProps={{ maxLength: 5 }}
          />
          <Select disabled={!isEditing} value={ampm} onChange={e => handleT(timeVal, e.target.value)} style={{ width: 70 }}>
            <MenuItem value="AM">AM</MenuItem>
            <MenuItem value="PM">PM</MenuItem>
          </Select>
        </div>
      );
    }

    if (rule.campo === 'CLIENTE') {
      return <AutocompleteAsyncrono disabled={!isEditing} label="Buscar Cliente" value={rule.valor} onChange={e => onChange("valor", e.target.value)} apiUrl="search/cliente" options={rule.opcionesAutocomplete || []} onOptionsChange={opts => onChange("opcionesAutocomplete", opts)} />;
    }

    if (rule.campo === 'CHOFER') {
      if (rule.operador === 'ES ALGUNO') {
        return (
          <div>
            {isEditing && <AutocompleteAsyncrono label="Añadir Chofer" apiUrl="search/chofer" value="" onChange={e => {
              if (!e.target.value) return;
              const valObj = (rule.opcionesAutocomplete || []).find(o => o.Value == e.target.value);
              if (valObj && !(rule.valoresMultiples || []).find(v => v.Value == valObj.Value)) {
                onChange("valoresMultiples", [...(rule.valoresMultiples || []), { Value: valObj.Value, Label: valObj.Label }]);
              }
            }} options={rule.opcionesAutocomplete || []} onOptionsChange={opts => onChange("opcionesAutocomplete", opts)} />}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 5 }}>
              {(rule.valoresMultiples || []).map((v, i) => (
                <div key={i} style={{ background: '#e0e0e0', padding: '2px 8px', borderRadius: 12, fontSize: 12, display: 'flex', alignItems: 'center' }}>
                  {v.Label}
                  {isEditing && <Icon style={{ fontSize: 14, cursor: 'pointer', marginLeft: 4 }} onClick={() => onChange("valoresMultiples", rule.valoresMultiples.filter((_, vi) => vi !== i))}>close</Icon>}
                </div>
              ))}
            </div>
          </div>
        );
      }
      return <AutocompleteAsyncrono disabled={!isEditing} label="Buscar Chofer" value={rule.valor} onChange={e => onChange("valor", e.target.value)} apiUrl="search/chofer" options={rule.opcionesAutocomplete || []} onOptionsChange={opts => onChange("opcionesAutocomplete", opts)} />;
    }

    if (rule.campo === 'SUCURSAL DESTINO') {
      return <Select disabled={!isEditing} value={rule.valor} onChange={e => onChange("valor", e.target.value)} style={{ minWidth: 200 }}>{catalogs_ref.sucursales.map(o => <MenuItem key={o.Value} value={o.Value}>{o.Label}</MenuItem>)}</Select>;
    }
    if (rule.campo === 'ZONA DESTINO') {
      return <Select disabled={!isEditing} value={rule.valor} onChange={e => onChange("valor", e.target.value)} style={{ minWidth: 200 }}>{catalogs_ref.zonas.map(o => <MenuItem key={o.Value} value={o.Value}>{o.Label}</MenuItem>)}</Select>;
    }
    if (rule.campo === 'TIPO ZONA DESTINO') {
      return <Select disabled={!isEditing} value={rule.valor} onChange={e => onChange("valor", e.target.value)} style={{ minWidth: 200 }}>{catalogs_ref.zona_tipos.map(o => <MenuItem key={o.Value} value={o.Value}>{o.Label}</MenuItem>)}</Select>;
    }
    if (rule.campo === 'TRANSPORTISTA') {
      return <Select disabled={!isEditing} value={rule.valor} onChange={e => onChange("valor", e.target.value)} style={{ minWidth: 200 }}>{(catalogs_ref.transportistas || []).map(o => <MenuItem key={o.Value} value={o.Value}>{o.Label}</MenuItem>)}</Select>;
    }

    return <TextField disabled={!isEditing} type="text" value={rule.valor} onChange={e => onChange("valor", e.target.value)} placeholder="Valor" style={{ width: 150 }} />;
  };

  // ========================= RENDER RULE ROW (shared) =========================

  const renderRuleRow = (rule, fieldOptions, onUpdate, onRemove, draggableProps = null, isEditing = true) => {
    const content = (
      <div style={{ display: 'flex', gap: 15, alignItems: 'center', marginBottom: 10, background: '#fff', padding: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
        {draggableProps && <div {...draggableProps}><Icon style={{ cursor: 'grab', color: '#999' }}>drag_indicator</Icon></div>}

        <Select disabled={!isEditing} value={rule.campo} onChange={e => onUpdate('campo', e.target.value)} style={{ width: 220 }}>
          {Object.keys(fieldOptions).map(key => <MenuItem key={key} value={key}>{key}</MenuItem>)}
        </Select>

        {(fieldOptions[rule.campo] || []).length > 0 && (
          <Select disabled={!isEditing} value={rule.operador} onChange={e => onUpdate('operador', e.target.value)} style={{ width: 150, color: 'red', fontWeight: 'bold' }}>
            {fieldOptions[rule.campo].map(op => <MenuItem key={op} value={op}>{op}</MenuItem>)}
          </Select>
        )}

        <div style={{ flex: 1 }}>
          {renderValueInput(rule, onUpdate, catalogs, isEditing)}
        </div>

        <IconButton size="small" onClick={onRemove}><Icon>delete</Icon></IconButton>
      </div>
    );
    return content;
  };

  // ========================= RENDER PAGO CONFIG =========================

  const renderPagoConfig = (config, onUpdate, isEditing) => (
    <div style={{ display: 'flex', gap: 15, alignItems: 'center' }}>
      <Typography variant="subtitle2">Se pagará:</Typography>
      <Select disabled={!isEditing} value={config.tipo_pago} onChange={e => onUpdate('tipo_pago', e.target.value)}>
        <MenuItem value="Monto fijo">Monto fijo</MenuItem>
        <MenuItem value="Porcentaje Total">Porcentaje Total</MenuItem>
        <MenuItem value="Porcentaje Subtotal">Porcentaje Subtotal</MenuItem>
        <MenuItem value="Porcentaje Entrega">Porcentaje Entrega</MenuItem>
      </Select>
      <TextField disabled={!isEditing} type="number" value={config.valor_pago}
        onChange={e => {
          let val = e.target.value;
          if (config.tipo_pago !== 'Monto fijo' && val > 100) return;
          if (val && val.includes('.')) {
            const parts = val.split('.');
            if (parts[1].length > 2) {
              val = `${parts[0]}.${parts[1].substring(0, 2)}`;
            }
          }
          onUpdate('valor_pago', val);
        }}
        InputProps={{ startAdornment: <Typography style={{ marginRight: 5 }}>{config.tipo_pago === 'Monto fijo' ? '$' : '%'}</Typography> }}
        style={{ width: 100 }}
      />
    </div>
  );

  // ========================= RENDER EMBALAJE SELECTOR =========================

  const renderEmbalajeSelector = (def, gIndex, dIndex, isEditing) => {
    const isIn = def.operador_embalaje === 'ES ALGUNO';
    // Filtrar comodin (ID 1) cuando es ES ALGUNO
    const embalajeOptions = isIn
      ? catalogs.embalajes.filter(e => String(e.Value) !== String(EMBALAJE_COMODIN_ID))
      : catalogs.embalajes;

    return (
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flex: 1 }}>
        <Typography variant="subtitle2" style={{ whiteSpace: 'nowrap' }}>EMBALAJE</Typography>

        <Select disabled={!isEditing} value={def.operador_embalaje} onChange={e => updateEmbalajeDef(gIndex, dIndex, 'operador_embalaje', e.target.value)}
          style={{ width: 130, color: 'red', fontWeight: 'bold' }}>
          <MenuItem value="=">=</MenuItem>
          <MenuItem value="ES ALGUNO">ES ALGUNO</MenuItem>
        </Select>

        {!isIn ? (
          <Select disabled={!isEditing} value={def.valor_embalaje} onChange={e => updateEmbalajeDef(gIndex, dIndex, 'valor_embalaje', e.target.value)}>
            {embalajeOptions.map(o => <MenuItem key={o.Value} value={o.Value}>{o.Label}</MenuItem>)}
          </Select>
        ) : (
          <div style={{ flex: 1 }}>
            {isEditing && <Select value="" displayEmpty onChange={e => {
              if (!e.target.value) return;
              const opt = embalajeOptions.find(o => String(o.Value) === String(e.target.value));
              if (opt && !(def.valoresMultiples || []).find(v => v.Value == opt.Value)) {
                updateEmbalajeDef(gIndex, dIndex, 'valoresMultiples', [...(def.valoresMultiples || []), { Value: opt.Value, Label: opt.Label }]);
              }
            }} style={{ minWidth: 200 }}>
              <MenuItem value="" disabled>Añadir embalaje...</MenuItem>
              {embalajeOptions.map(o => <MenuItem key={o.Value} value={o.Value}>{o.Label}</MenuItem>)}
            </Select>}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 5 }}>
              {(def.valoresMultiples || []).map((v, i) => (
                <div key={i} style={{ background: '#e0e0e0', padding: '2px 8px', borderRadius: 12, fontSize: 12, display: 'flex', alignItems: 'center' }}>
                  {v.Label}
                  {isEditing && <Icon style={{ fontSize: 14, cursor: 'pointer', marginLeft: 4 }} onClick={() => {
                    updateEmbalajeDef(gIndex, dIndex, 'valoresMultiples', (def.valoresMultiples || []).filter((_, vi) => vi !== i));
                  }}>close</Icon>}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  // ========================= RENDER GRUPO GUIA =========================

  const renderGrupoGuia = (grupo, gIndex) => (
    <Collapse in={grupo.isEditing || !grupo.collapsed}>
      <CardContent>
        {(grupo.subgrupos || []).map((subgrupo, sgIndex) => (
          <Card key={String(subgrupo.id)} style={{ marginBottom: 15, padding: 10, border: '1px solid #1976d2' }} variant="outlined">
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 15 }}>
              {renderPagoConfig(subgrupo, (key, val) => updateSubGroup(gIndex, sgIndex, key, val), grupo.isEditing)}
              <IconButton size="small" color="secondary" onClick={() => removeSubGroup(gIndex, sgIndex)}><Icon>close</Icon></IconButton>
            </div>

            <div style={{ marginBottom: 10 }}>
              <Typography variant="subtitle2" style={{ color: 'red', fontWeight: 'bold' }}>Cuando TODO lo siguiente se cumpla:</Typography>
            </div>

            <Droppable droppableId={`guia-${gIndex}-${sgIndex}`} type="rule">
              {(provided) => (
                <div ref={provided.innerRef} {...provided.droppableProps} style={{ padding: 10, minHeight: 50, background: '#fafafa', border: '1px dashed #ccc' }}>
                  {(subgrupo.reglas || []).map((rule, rIndex) => (
                    <Draggable draggableId={String(rule.id)} index={rIndex} key={String(rule.id)}>
                      {(provided) => (
                        <div ref={provided.innerRef} {...provided.draggableProps} style={provided.draggableProps.style}>
                          {renderRuleRow(
                            rule,
                            GUIA_FIELDS,
                            (key, val) => updateRule(gIndex, sgIndex, rIndex, key, val),
                            () => removeRule(gIndex, sgIndex, rIndex),
                            provided.dragHandleProps,
                            grupo.isEditing
                          )}
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                  {grupo.isEditing && <Button size="small" variant="text" onClick={() => addRule(gIndex, sgIndex)} style={{ marginTop: 5 }}>+ Añadir Regla</Button>}
                </div>
              )}
            </Droppable>
          </Card>
        ))}
        {grupo.isEditing && <Button size="small" variant="outlined" onClick={() => addSubGroup(gIndex)}>+ Añadir Subgrupo</Button>}
      </CardContent>
    </Collapse>
  );

  // ========================= RENDER GRUPO EMBALAJE =========================

  const renderGrupoEmbalaje = (grupo, gIndex) => (
    <Collapse in={grupo.isEditing || !grupo.collapsed}>
      <CardContent>
        {/* Reglas de grupo (precondiciones) */}
        <Card style={{ marginBottom: 15, padding: 10, border: '1px solid #4caf50' }} variant="outlined">
          <Typography variant="subtitle1" style={{ fontWeight: 'bold', marginBottom: 10, color: '#4caf50' }}>
            <Icon style={{ verticalAlign: 'middle', marginRight: 5 }}>filter_list</Icon>
            Reglas del Grupo (precondiciones)
          </Typography>
          <div style={{ padding: 10, background: '#fafafa', border: '1px dashed #ccc', minHeight: 40 }}>
            {(grupo.grupo_reglas || []).map((rule, rIndex) => (
              <div key={rule.id || rIndex}>
                {renderRuleRow(
                  rule,
                  GUIA_FIELDS,
                  (key, val) => updateGrupoRegla(gIndex, rIndex, key, val),
                  () => removeGrupoRegla(gIndex, rIndex),
                  null,
                  grupo.isEditing
                )}
              </div>
            ))}
            {grupo.isEditing && <Button size="small" variant="text" onClick={() => addGrupoRegla(gIndex)}>+ Añadir Regla de Grupo</Button>}
          </div>
        </Card>

        {/* Definiciones de embalaje */}
        <Card style={{ padding: 10, border: '1px solid #ff9800' }} variant="outlined">
          <Typography variant="subtitle1" style={{ fontWeight: 'bold', marginBottom: 10, color: '#ff9800' }}>
            <Icon style={{ verticalAlign: 'middle', marginRight: 5 }}>inventory_2</Icon>
            Definir Embalaje
          </Typography>

          <Droppable droppableId={`embdefs-${gIndex}`} type="embalaje-def">
            {(provided) => (
              <div ref={provided.innerRef} {...provided.droppableProps}>
                {(grupo.embalaje_defs || []).map((def, dIndex) => (
                  <Draggable draggableId={String(def.id)} index={dIndex} key={String(def.id)}>
                    {(provided) => (
                      <div ref={provided.innerRef} {...provided.draggableProps} style={{ ...provided.draggableProps.style, marginBottom: 15, padding: 12, background: '#fff8e1', border: '1px solid #ffcc80', borderRadius: 4 }}>
                        {/* Header: drag + embalaje selector + pago config + delete */}
                        <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 10, flexWrap: 'wrap' }}>
                          <div {...provided.dragHandleProps}>
                            <Icon style={{ cursor: 'grab', color: '#999' }}>drag_indicator</Icon>
                          </div>

                          {renderEmbalajeSelector(def, gIndex, dIndex, grupo.isEditing)}

                          {renderPagoConfig(def, (key, val) => updateEmbalajeDef(gIndex, dIndex, key, val), grupo.isEditing)}

                          <IconButton size="small" color="secondary" onClick={() => removeEmbalajeDef(gIndex, dIndex)}>
                            <Icon>delete</Icon>
                          </IconButton>
                        </div>

                        {/* Sub-reglas (PIEZAS EMBALAJE, PESO EMBALAJE) */}
                        {(def.subreglas || []).length > 0 && (
                          <div style={{ marginLeft: 40, padding: 8, background: '#fff', border: '1px dashed #ccc', borderRadius: 4, marginBottom: 5 }}>
                            <Typography variant="caption" style={{ color: '#666', marginBottom: 5, display: 'block' }}>Si además:</Typography>
                            {def.subreglas.map((sr, srIndex) => (
                              <div key={sr.id || srIndex} style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 5 }}>
                                <Select disabled={!grupo.isEditing} value={sr.campo} onChange={e => updateEmbalajeSubregla(gIndex, dIndex, srIndex, 'campo', e.target.value)} style={{ width: 180 }}>
                                  {Object.keys(EMBALAJE_SUB_FIELDS).map(k => <MenuItem key={k} value={k}>{k}</MenuItem>)}
                                </Select>
                                <Select disabled={!grupo.isEditing} value={sr.operador} onChange={e => updateEmbalajeSubregla(gIndex, dIndex, srIndex, 'operador', e.target.value)}
                                  style={{ width: 80, color: 'red', fontWeight: 'bold' }}>
                                  {EMBALAJE_SUB_FIELDS[sr.campo]?.map(op => <MenuItem key={op} value={op}>{op}</MenuItem>)}
                                </Select>
                                <TextField disabled={!grupo.isEditing} type="number" value={sr.valor} onChange={e => updateEmbalajeSubregla(gIndex, dIndex, srIndex, 'valor', e.target.value)} placeholder="Valor" style={{ width: 120 }} />
                                <IconButton size="small" onClick={() => removeEmbalajeSubregla(gIndex, dIndex, srIndex)}><Icon>delete</Icon></IconButton>
                              </div>
                            ))}
                          </div>
                        )}
                        {grupo.isEditing && <Button size="small" variant="text" onClick={() => addEmbalajeSubregla(gIndex, dIndex)} style={{ marginLeft: 40 }}>
                          + Añadir sub-regla (piezas/peso)
                        </Button>}
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>

          {grupo.isEditing && <Button size="small" variant="outlined" onClick={() => addEmbalajeDef(gIndex)} style={{ marginTop: 10 }}>
            + Añadir Embalaje
          </Button>}
        </Card>
      </CardContent>
    </Collapse>
  );

  // ========================= RENDER MAIN =========================

  if (loading) return <div style={{ padding: 20 }}><Typography>Cargando reglas...</Typography></div>;

  return (
    <div style={{ padding: 20 }}>
      <Typography variant="h4" gutterBottom>Constructor de Reglas del Transportista</Typography>
      <div style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
        <Button variant="contained" color="primary" onClick={() => addGroup('Guia')} startIcon={<Icon>add</Icon>}>
          Grupo Guía
        </Button>
        <Button variant="contained" style={{ background: '#ff9800', color: '#fff' }} onClick={() => addGroup('Embalaje')} startIcon={<Icon>inventory_2</Icon>}>
          Grupo Embalaje
        </Button>
        <Button variant="contained" color="secondary" onClick={saveRules} startIcon={<Icon>save</Icon>}>
          Guardar Reglas
        </Button>
      </div>

      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="board" type="group">
          {(provided) => (
            <div ref={provided.innerRef} {...provided.droppableProps}>
              {state.map((grupo, gIndex) => (
                <Draggable draggableId={String(grupo.id)} index={gIndex} key={String(grupo.id)}>
                  {(provided) => (
                    <Card
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      style={{
                        ...provided.draggableProps.style,
                        marginBottom: 20,
                        marginTop: 10,
                        border: grupo.esquema === 'Embalaje' ? '2px solid #ff9800' : '2px solid #ccc',
                        borderRadius: 8
                      }}
                    >
                      <div style={{
                        padding: 10,
                        background: grupo.esquema === 'Embalaje' ? '#fff3e0' : '#f5f5f5',
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div {...provided.dragHandleProps}>
                            <Icon style={{ cursor: 'grab' }}>drag_indicator</Icon>
                          </div>
                          <TextField disabled={!grupo.isEditing} value={grupo.nombre_grupo} onChange={e => updateGroupName(gIndex, e.target.value)} placeholder="Nombre del Grupo" />
                          <Typography variant="caption" style={{
                            background: grupo.esquema === 'Embalaje' ? '#ff9800' : '#1976d2',
                            color: '#fff', padding: '2px 8px', borderRadius: 4, fontWeight: 'bold'
                          }}>
                            {grupo.esquema === 'Embalaje' ? '📦 EMBALAJE' : '📋 GUÍA'}
                          </Typography>
                        </div>
                        <div style={{ display: 'flex', gap: 5 }}>
                          <Button size="small" variant={grupo.isEditing ? "contained" : "outlined"} color="primary" onClick={() => toggleGroupEdit(gIndex)}>
                            {grupo.isEditing ? 'VER MODO' : 'EDITAR'}
                          </Button>
                          {!grupo.isEditing && (
                            <IconButton size="small" onClick={() => toggleGroupCollapse(gIndex)}>
                              <Icon>{grupo.collapsed ? 'expand_more' : 'expand_less'}</Icon>
                            </IconButton>
                          )}
                          <IconButton size="small" color="secondary" onClick={() => removeGroup(gIndex)}><Icon>delete</Icon></IconButton>
                        </div>
                      </div>

                      {grupo.esquema === 'Embalaje'
                        ? renderGrupoEmbalaje(grupo, gIndex)
                        : renderGrupoGuia(grupo, gIndex)
                      }
                    </Card>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </div>
  );
}
